/**
 * pdfExport.ts
 *
 * Renders every slide in the active presentation to an image via Office.js
 * and assembles them into one downloadable PDF using jsPDF. Requires
 * PowerPointApi 1.5+ (Slide.getImageAsBase64) — the caller surfaces the
 * resulting error as a panel status message, never a console call.
 */
import { jsPDF } from "jspdf";
import { logOfficeError } from "../../../../../telemetry";
import { hasPpt, ppt } from "../../../utils/formatUtils";
import { SLIDE_WIDTH, SLIDE_HEIGHT } from "../../../const";
import { downloadBytes } from "../../../utils/officePptx";
import type { PdfExportProgress } from "../../../types";

/** Slide images are captured at this multiple of the page's point size for
 * a sharper render than a 1:1 pixel/point capture would give. */
const CAPTURE_SCALE = 2;

type SlideImageCapable = PowerPoint.Slide & {
  getImageAsBase64?: (opts: { width: number; height: number }) => OfficeExtension.ClientResult<string>;
};

async function captureSlideImages(onProgress?: (progress: PdfExportProgress) => void): Promise<string[]> {
  if (!hasPpt()) throw new Error("PowerPoint host unavailable — is the add-in still attached?");

  return new Promise<string[]>((resolve, reject) => {
    ppt()
      .run(async (context) => {
        const slides = context.presentation.slides;
        slides.load("items");
        await context.sync();

        const total = slides.items.length;
        if (total === 0) {
          resolve([]);
          return;
        }

        const first = slides.items[0] as SlideImageCapable;
        if (typeof first.getImageAsBase64 !== "function") {
          reject(new Error(
            "Exporting to PDF needs PowerPointApi 1.5+ — try PowerPoint on the web, or update desktop PowerPoint.",
          ));
          return;
        }

        const images: string[] = [];
        for (let i = 0; i < total; i++) {
          const slide = slides.items[i] as SlideImageCapable;
          const result = slide.getImageAsBase64?.({
            width: SLIDE_WIDTH * CAPTURE_SCALE,
            height: SLIDE_HEIGHT * CAPTURE_SCALE,
          });
          if (!result) continue; // feature-detected above; guards a mid-loop host quirk
          await context.sync(); // NOSONAR ordered Office.js operations on a shared context
          images.push(result.value);
          onProgress?.({ current: i + 1, total });
        }
        resolve(images);
      })
      .catch(reject);
  });
}

/** Assembles captured slide images into a PDF sized to the deck's own
 * 16:9 point geometry (SLIDE_WIDTH/SLIDE_HEIGHT), one page per slide. */
function buildPdfDocument(images: string[]): jsPDF {
  const doc = new jsPDF({ orientation: "l", unit: "pt", format: [SLIDE_WIDTH, SLIDE_HEIGHT] });
  images.forEach((base64, index) => {
    if (index > 0) doc.addPage([SLIDE_WIDTH, SLIDE_HEIGHT], "l");
    doc.addImage(base64, "PNG", 0, 0, SLIDE_WIDTH, SLIDE_HEIGHT);
  });
  return doc;
}

/** Captures every slide and downloads the resulting PDF as `filename`. */
export async function exportPresentationAsPdf(
  filename: string,
  onProgress?: (progress: PdfExportProgress) => void,
): Promise<void> {
  try {
    const images = await captureSlideImages(onProgress);
    if (images.length === 0) throw new Error("This presentation has no slides to export.");
    const pdfBytes = buildPdfDocument(images).output("arraybuffer");
    downloadBytes(pdfBytes, filename, "application/pdf");
  } catch (err) {
    logOfficeError(err, "share-export-pdf");
    throw err instanceof Error ? err : new Error("Could not export this presentation as a PDF.");
  }
}
